// Leaderboard.jsx

import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, limit, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import toast from 'react-hot-toast';
import Navbar from '../components/Navbar';
import { auth } from '../firebase'; 

export default function Leaderboard() {
  const [players, setPlayers] = useState([]);
  const currentUserId = auth.currentUser ? auth.currentUser.uid : null;

  useEffect(() => {
    const fetchLeaderboard = async () => {
      const leaderboardRef = collection(db, 'leaderboard_master');
      const q = query(leaderboardRef, orderBy('chronos', 'desc'), limit(50));
      const querySnapshot = await getDocs(q);
      const rawData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      const enriched = await Promise.all(
        rawData.map(async player => {
          const userRef = doc(db, 'users', player.id);
          const userSnap = await getDoc(userRef);
          const avatar = userSnap.exists() ? userSnap.data().avatar || '' : '';
          const bestReactionTime = player.bestReactionTime || '-';
          return { ...player, avatar, bestReactionTime };
        })
      );

      setPlayers(enriched);

      const currentIndex = enriched.findIndex(p => p.id === currentUserId);
      const currentZone = currentIndex < 10 ? 'Elite Zone' : currentIndex < 30 ? 'Rising Stars' : currentIndex < 50 ? 'Challenger Zone' : 'None';
      const lastZone = localStorage.getItem('lastZone');

      if (currentZone !== lastZone && currentZone !== 'None') {
        toast(`✨ You've entered the ${currentZone}!`, {
          duration: 3000,
          position: 'bottom-center',
          style: { background: '#ede9fe', color: '#5b21b6', fontWeight: 'bold' }
        });
        localStorage.setItem('lastZone', currentZone);
      }
      const lastRank = parseInt(localStorage.getItem('lastRank') || 99);
      if (currentIndex !== -1 && currentIndex < lastRank) {
        const overtaken = enriched[lastRank - 1]?.name || 'someone';
        toast.success(`🎉 You just overtook ${overtaken} and moved to Rank #${currentIndex + 1}!`, {
          duration: 4000,
          position: 'top-center'
        });
        localStorage.setItem('lastRank', currentIndex);
      }
    };
    fetchLeaderboard();
  }, []);

  return (
    <>
      <Navbar />
      <div className="min-h-screen flex flex-col items-center justify-start p-6 bg-gradient-to-tr from-yellow-100 via-white to-purple-100">
        <h1 className="text-3xl font-bold text-purple-800 mb-2">🏆 Brainy Leaderboard</h1>
        <p className="text-md text-purple-700 mb-4 italic">Only the top 50 Brainies are featured in this elite chart.</p>
        <div className="w-full max-w-6xl overflow-x-auto">
          {players.length === 0 ? (
            <div className="text-center text-purple-600 text-lg font-semibold mt-10">
              🚀 Be the first Brainy Buddy to shine on the Leaderboard! 🧠✨
            </div>
          ) : (
            <table className="w-full text-center table-auto border-collapse">
              <thead>
                <tr className="bg-purple-300">
                  <th className="p-2">Rank</th>
                  <th className="p-2">Player</th>
                  <th className="p-2">Chronos</th>
                  <th className="p-2">Max. Digits</th>
                  <th className="p-2">Focus Streak</th>
                  <th className="p-2">Synonym Syncs</th>
                  <th className="p-2">Math Sprint</th>
                  <th className="p-2">Logic Score</th>
                  <th className="p-2">Reaction Time</th>
                </tr>
              </thead>
              <tbody>
                {players.map((player, index) => {
                  const zoneHeader =
                    index === 0 ? '🌟 Elite Zone' :
                    index === 10 ? '🚀 Rising Stars' :
                    index === 25 ? '📚 Challenger Zone' : null;

                  const zone =
                    index < 10 ? 'bg-yellow-100'
                    : index < 25 ? 'bg-blue-50'
                    : 'bg-purple-50';

                  return (
                    <React.Fragment key={player.id}>
                      {zoneHeader && (
                        <tr>
                          <td colSpan="9" className="py-2 text-left text-sm font-bold text-purple-600 bg-white">
                            {zoneHeader}
                          </td>
                        </tr>
                      )}
                      <tr
                        className={`even:bg-purple-100 hover:bg-yellow-100 transition-colors duration-200 ${zone} ${
                          player.id === currentUserId ? 'bg-green-100 font-bold' : ''
                        }`}
                      >
                        <td className={`p-2 font-semibold ${
                          index === 0 ? 'text-yellow-500' : 
                          index === 1 ? 'text-gray-400' : 
                          index === 2 ? 'text-orange-500' : ''
                        }`}>
                          {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : index + 1}
                        </td>
                        <td className="p-2 flex items-center gap-2 justify-center">
                          {player.avatar && player.avatar.startsWith('/avatars') ? (
                            <img
                              src={player.avatar}
                              alt="avatar"
                              className="w-6 h-6 rounded-full object-cover border border-purple-300"
                            />
                          ) : player.avatar ? (
                            <span className="text-xl">{player.avatar}</span>
                          ) : (
                            <span className="text-xl">👤</span>
                          )}
                          <span>{player.name}</span>
                        </td>
                        <td className="p-2">{player.chronos}</td>
                        <td className="p-2">{player.bestNumberMemoryLevel || '-'}</td>
                        <td className="p-2">{player.bestFocusGameStreak || '-'}</td>
                        <td className="p-2">{player.bestSynonymWords || '-'}</td>
                        <td className="p-2">{player.bestMathSprintScore || '-'}</td>
                        <td className="p-2">{player.bestLogicScore || '-'}</td>
                        <td className="p-2">{player.bestReactionTime !== '-' ? `${player.bestReactionTime} ms` : '-'}</td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}
